<template>
  <span>
    <v-dialog v-model="showDialog" width="500" persistent>
      <v-card>
        <v-card-title class="headline lighten-2" primary-title>
          {{ dialogTitle }}
        </v-card-title>

        <v-icon class="close-button" color="grey" @click="closeDialog">close</v-icon>

        <file-upload
          v-if="isInitial"
          :upload-field-name="uploadFieldName"
          :is-saving="isSaving"
          source="linePricingGroups"
          @save="save"
        />

        <div v-if="isSaving && !isFailed">
          <v-card-text>
            <div class="container grid-list-md">
              <div class="layout wrap">
                <div class="flex xs12">
                  <div class="v-input">
                    {{ $t('settings.architecture.linePricingGroups.upload.uploadingState') }}
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </div>

        <div v-if="isValidated">
          <v-card-text>
            <div class="container grid-list-md">
              <div class="layout wrap">
                <div class="flex xs12">
                  <div class="dialog-copy">
                    <div>
                      <v-icon color="green darken-2">check_circle</v-icon>
                      {{
                        $t('settings.architecture.linePricingGroups.upload.validationSuccessful')
                      }}
                    </div>
                    <br />
                    <v-flex>
                      {{ $t('settings.architecture.linePricingGroups.upload.articlesReceived') }}:
                      <strong>{{ counts.articlesReceived }}</strong>
                    </v-flex>
                    <v-flex>
                      {{ $t('settings.architecture.linePricingGroups.upload.articlesToUnassign') }}:
                      <strong>{{ counts.articlesToUnassign }}</strong>
                    </v-flex>
                    <v-flex v-if="isArchitectureGroupImport">
                      <strong
                        >{{
                          $t('settings.architecture.linePricingGroups.upload.importScope')
                        }}:</strong
                      >
                      {{ $t('settings.architecture.linePricingGroups.upload.architectureGroup') }}
                    </v-flex>
                    <br />
                    <v-flex>
                      <span v-if="counts.articlesToUnassign > 0">
                        {{
                          $t(
                            'settings.architecture.linePricingGroups.upload.aboutToUnassignLinePricingGroups'
                          )
                        }}
                      </span>
                      {{ $t('settings.architecture.linePricingGroups.upload.confirmUpload') }}
                    </v-flex>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" class="dialog-button" outlined depressed @click="reset()">{{
              $t('actions.cancel')
            }}</v-btn>
            <v-btn
              class="primary dialog-button"
              depressed
              :loading="busyImportingLinePricingGroups"
              @click="confirm()"
              >{{ $t('actions.upload') }}</v-btn
            >
          </v-card-actions>
        </div>

        <div v-if="isFailed">
          <v-card-text>
            <div class="container grid-list-md">
              <div class="layout wrap">
                <div class="flex xs12">
                  <template v-if="hasUnhandledError">
                    <upload-error-card
                      :error-message="$t('errors.generic')"
                      unique-key-prefix="genericError"
                    />
                  </template>
                  <template v-else-if="uploadError.tooManyRows">
                    <div class="v-input">
                      {{
                        $t('settings.architecture.linePricingGroups.upload.tooManyRows', {
                          maxRows,
                        })
                      }}
                    </div>
                  </template>
                  <template v-else>
                    <upload-error-card
                      v-if="uploadError.duplicateProductKeys.length"
                      :error-message="
                        $t('settings.architecture.linePricingGroups.upload.duplicateProductKey')
                      "
                      :details="uploadError.duplicateProductKeys"
                      unique-key-prefix="duplicateProductKey"
                    />
                    <upload-error-card
                      v-if="uploadError.productKeysWithIncorrectTSGs.length"
                      :error-message="$t('attributes.upload.incorrectToolStoreGroups')"
                      :details="uploadError.productKeysWithIncorrectTSGs"
                      unique-key-prefix="incorrectToolStoreGroups"
                    />
                    <upload-error-card
                      v-if="uploadError.productKeysWithInvalidLinePriceFactor.length"
                      :error-message="
                        $t(
                          'settings.architecture.linePricingGroups.upload.productKeysWithInvalidLinePriceFactor'
                        )
                      "
                      :details="uploadError.productKeysWithInvalidLinePriceFactor"
                      unique-key-prefix="productKeysWithInvalidLinePriceFactor"
                    />
                    <upload-error-card
                      v-if="uploadError.duplicateLinePricingGroupNames.length"
                      :error-message="
                        $t(
                          'settings.architecture.linePricingGroups.upload.duplicateLinePricingGroupNames'
                        )
                      "
                      :details="uploadError.duplicateLinePricingGroupNames"
                      unique-key-prefix="duplicateLinePricingGroupNames"
                    />
                    <upload-error-card
                      v-if="uploadError.productKeysWithMissingHierarchies.length"
                      :error-message="
                        $t('settings.architecture.linePricingGroups.upload.missingHierarchies')
                      "
                      :details="uploadError.productKeysWithMissingHierarchies"
                      unique-key-prefix="missingHierarchies"
                    />
                    <upload-error-card
                      v-if="
                        uploadError.productKeysWithInvalidSubgroups &&
                          uploadError.productKeysWithInvalidSubgroups.length
                      "
                      :error-message="
                        $t(
                          'settings.architecture.linePricingGroups.upload.productKeysWithInvalidSubgroups'
                        )
                      "
                      :details="uploadError.productKeysWithInvalidSubgroups"
                      unique-key-prefix="productKeysWithInvalidSubgroups"
                    />
                  </template>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn class="primary" @click="closeDialog()">{{ $t('actions.close') }}</v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { has, isEmpty, size, get } from 'lodash';
import states from '@enums/upload-states';

export default {
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      size,
      uploadedFiles: [],
      uploadError: null,
      currentStatus: states.initial,
      uploadFieldName: 'file',
    };
  },

  computed: {
    ...mapState('clientConfig', ['uploadConfig']),
    ...mapState('architectureGroup', ['counts', 'busyImportingLinePricingGroups']),

    maxRows() {
      return this.uploadConfig.linePricingGroupsUpload.maxRows;
    },

    isInitial() {
      return this.currentStatus === states.initial;
    },

    isSaving() {
      return this.currentStatus === states.saving;
    },

    isValidated() {
      return this.currentStatus === states.validated;
    },

    isFailed() {
      return this.currentStatus === states.failed;
    },

    hasUnhandledError() {
      // if neither key exists, show generic error
      return (
        !isEmpty(this.uploadError) &&
        !has(this.uploadError, 'tooManyRows') &&
        !has(this.uploadError, 'duplicateLinePricingGroupNames')
      );
    },
    isArchitectureGroupImport() {
      return !!get(this.params, 'architectureGroupId');
    },

    dialogTitle() {
      return this.isArchitectureGroupImport
        ? this.$t('settings.architecture.linePricingGroups.upload.dialogHeadingAG')
        : this.$t('settings.architecture.linePricingGroups.upload.dialogHeading');
    },
  },

  mounted() {
    this.reset();
  },

  methods: {
    ...mapActions('architectureGroup', ['uploadLPGDataFile', 'confirmLGPUpload']),

    closeDialog() {
      this.$emit('closeDialog');
      this.reset();
    },

    async save(formData) {
      try {
        this.currentStatus = states.saving;
        if (this.isArchitectureGroupImport) {
          formData.append('architectureGroupId', get(this.params, 'architectureGroupId'));
        }
        const res = await this.uploadLPGDataFile(formData);
        this.currentStatus = res.error ? states.brokenValidations : states.validated;
      } catch (err) {
        this.currentStatus = states.failed;
        this.uploadError = err;
      }
    },
    async confirm() {
      try {
        await this.confirmLGPUpload({
          params: this.params,
          primaryColumn: this.$t('pricing.productKey'),
        });
        this.closeDialog();
      } catch (err) {
        this.uploadError = err.response;
        this.currentStatus = states.failed;
      }
    },

    reset() {
      this.currentStatus = states.initial;
      this.uploadedFiles = [];
      this.uploadError = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-copy {
  font-size: 1.6rem;
}
</style>
